import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getArDateKey } from "@/lib/timezone";

type ProgressRow = {
  day_number: number;
  completed_at: string;
};

export type ProgressEntry = ProgressRow & {
  completed_date: string;
};

const DATE_ONLY_UTC_RE = /^(\d{4})-(\d{2})-(\d{2})[T\s]00:00:00(?:\.0+)?(?:Z|\+00(?::00)?)$/;

const toDateKey = (date: string | Date) => {
  if (typeof date === "string") {
    const dateOnlyUtcMatch = date.match(DATE_ONLY_UTC_RE);
    if (dateOnlyUtcMatch) {
      return `${dateOnlyUtcMatch[1]}-${dateOnlyUtcMatch[2]}-${dateOnlyUtcMatch[3]}`;
    }
  }
  // Always interpret timestamps in Argentina time so the calendar key
  // doesn't shift based on the user's device timezone.
  return getArDateKey(date);
};

export function useProgress(userId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: progressData = [], isLoading } = useQuery({
    queryKey: ["progress", userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("user_progress")
        .select("day_number, completed_at")
        .eq("user_id", userId)
        .order("day_number", { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  const progressEntries: ProgressEntry[] = useMemo(() => (
    progressData
      .map((d) => ({
        ...d,
        completed_date: toDateKey(d.completed_at),
      }))
      .sort((a, b) => a.day_number - b.day_number)
  ), [progressData]);
  const completedDays = useMemo(() => (
    Array.from(new Set(progressEntries.map((d) => d.day_number))).sort((a, b) => a - b)
  ), [progressEntries]);
  const completedDates = useMemo(() => (
    Array.from(new Set(progressEntries.map((d) => d.completed_date))).sort()
  ), [progressEntries]);

  const toggleDay = useMutation({
    mutationFn: async ({
      dayNumber,
      completedAt,
      force,
    }: {
      dayNumber: number;
      completedAt?: string;
      force?: "complete" | "incomplete";
    }) => {
      if (!userId) throw new Error("Not authenticated");

      const { data: existing, error: lookupError } = await supabase
        .from("user_progress")
        .select("day_number")
        .eq("user_id", userId)
        .eq("day_number", dayNumber)
        .maybeSingle();
      if (lookupError) throw lookupError;

      const shouldComplete = force === "complete" ? true : force === "incomplete" ? false : !existing;

      if (!shouldComplete) {
        if (!existing) return;
        const { error } = await supabase
          .from("user_progress")
          .delete()
          .eq("user_id", userId)
          .eq("day_number", dayNumber);
        if (error) throw error;
      } else {
        if (existing) return;
        const insertData: { user_id: string; day_number: number; completed_at?: string } = { user_id: userId, day_number: dayNumber };
        if (completedAt) insertData.completed_at = completedAt;
        const { error } = await supabase
          .from("user_progress")
          .insert(insertData);
        if (error) throw error;
      }
    },
    onMutate: async ({ dayNumber, completedAt, force }) => {
      await queryClient.cancelQueries({ queryKey: ["progress", userId] });
      const previousProgress = queryClient.getQueryData<ProgressRow[]>(["progress", userId]);

      queryClient.setQueryData<ProgressRow[]>(["progress", userId], (old = []) => {
        const exists = old.some((row) => row.day_number === dayNumber);
        const shouldComplete = force === "complete" ? true : force === "incomplete" ? false : !exists;

        if (!shouldComplete) return old.filter((row) => row.day_number !== dayNumber);
        if (exists) return old;
        return [...old, { day_number: dayNumber, completed_at: completedAt ?? new Date().toISOString() }];
      });

      return { previousProgress };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousProgress) {
        queryClient.setQueryData(["progress", userId], context.previousProgress);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["progress", userId] });
    },
  });

  const clearCalendarDate = useMutation({
    mutationFn: async ({ dayNumbers }: { dayNumbers: number[] }) => {
      if (!userId) throw new Error("Not authenticated");
      if (dayNumbers.length === 0) return;
      const { error } = await supabase
        .from("user_progress")
        .delete()
        .eq("user_id", userId)
        .in("day_number", dayNumbers);
      if (error) throw error;
    },
    onMutate: async ({ dayNumbers }) => {
      await queryClient.cancelQueries({ queryKey: ["progress", userId] });
      const previousProgress = queryClient.getQueryData<ProgressRow[]>(["progress", userId]);

      queryClient.setQueryData<ProgressRow[]>(["progress", userId], (old = []) =>
        old.filter((row) => !dayNumbers.includes(row.day_number))
      );

      return { previousProgress };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousProgress) {
        queryClient.setQueryData(["progress", userId], context.previousProgress);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["progress", userId] });
    },
  });

  return { completedDays, completedDates, progressEntries, isLoading, toggleDay, clearCalendarDate };
}
